import React, { useCallback, useEffect, useRef, useState } from 'react';
import Loading from '../../../components/common/Loading';
import reolinkVideoApi, { resolveReolinkVideoId } from '../../../services/reolinkVideoApi';
import { formatReolinkRecordingClock } from '../utils/reolinkCameraBattery';

/** Refresh before ~15 min expiry. */
const REFRESH_BUFFER_MS = 60 * 1000;

function formatDateTime(video) {
  return formatReolinkRecordingClock(video);
}

function getViewerErrorMessage(err) {
  const msg = err?.message || '';
  const status = err?.status;
  if (status === 401) return 'You are not authorized to view this video.';
  if (status === 403 || /forbidden|no access/i.test(msg)) {
    return 'No access to this bridge video.';
  }
  if (status === 404 || /not found/i.test(msg)) return 'Video not found.';
  if (/unavailable|network|timeout|503/i.test(msg)) {
    return 'Video service unavailable. Please try again.';
  }
  return msg || 'Failed to load video.';
}

/**
 * Play: GET /api/bridges/{bridgeId}/reolink-videos/{id}/url
 * then <video src={data.url} controls autoPlay playsInline />
 * On expiry / error, call /url again.
 */
export default function ReolinkVideoViewer({ bridgeId, video, onClose }) {
  const [mediaUrl, setMediaUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState(video?.fileName || 'Video');
  const [downloading, setDownloading] = useState(false);
  const openRef = useRef(true);
  const videoRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const refreshAttemptRef = useRef(0);
  const playedRef = useRef(false);
  const loadingRef = useRef(false);
  const videoId = resolveReolinkVideoId(video);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const loadUrl = useCallback(async ({ silent = false } = {}) => {
    if (!bridgeId || !videoId) {
      setLoading(false);
      setError('Missing bridge or video id.');
      return;
    }
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      const result = await reolinkVideoApi.getPlayUrl(bridgeId, videoId);
      if (!openRef.current) return;
      setMediaUrl(result.url);
      if (result.fileName) setFileName(result.fileName);
      setLoading(false);
      if (!silent) setError(null);

      clearRefreshTimer();
      const expiresIn = result.expiresIn || 900;
      const refreshIn = Math.max((expiresIn * 1000) - REFRESH_BUFFER_MS, 30 * 1000);
      refreshTimerRef.current = setTimeout(() => {
        if (openRef.current) loadUrl({ silent: true });
      }, refreshIn);
    } catch (err) {
      if (!openRef.current) return;
      if (!silent) setMediaUrl(null);
      setLoading(false);
      setError(getViewerErrorMessage(err));
    } finally {
      loadingRef.current = false;
    }
  }, [bridgeId, videoId, clearRefreshTimer]);

  useEffect(() => {
    openRef.current = true;
    refreshAttemptRef.current = 0;
    playedRef.current = false;
    loadUrl();
    return () => {
      openRef.current = false;
      clearRefreshTimer();
    };
  }, [loadUrl, clearRefreshTimer]);

  const handleClose = () => {
    openRef.current = false;
    clearRefreshTimer();
    setMediaUrl(null);
    onClose?.();
  };

  const handleMediaError = () => {
    if (!openRef.current) return;
    const el = videoRef.current;
    // 1–3s clips often fire a decode/network error at end — do not reload (that flickers the app).
    if (playedRef.current || el?.ended || (el && el.currentTime > 0.15)) return;
    if (refreshAttemptRef.current < 1) {
      refreshAttemptRef.current += 1;
      loadUrl();
      return;
    }
    setError('Video failed to load. The URL may have expired — try again.');
  };

  const handleReplay = () => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = 0;
    const playPromise = el.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  };

  const handleDownload = async () => {
    if (!mediaUrl || downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(mediaUrl);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName || 'video.mp4';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      const a = document.createElement('a');
      a.href = mediaUrl;
      a.download = fileName || 'video.mp4';
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setDownloading(false);
    }
  };

  const { date, time } = formatDateTime({
    fileName: fileName || video?.fileName,
    s3Key: video?.s3Key,
    recordedAt: video?.recordedAt,
  });

  return (
    <div className="alerts-recording-viewer-inline">
      <div className="alerts-recording-viewer-header">
        <div className="alerts-recording-viewer-meta">
          <div><strong>Date:</strong> {date}</div>
          <div><strong>Time:</strong> {time}</div>
        </div>
        <div className="alerts-recording-viewer-actions">
          <button
            type="button"
            className="btn btn-blue btn-sm alerts-recording-viewer-icon-btn"
            title="Download"
            aria-label="Download"
            disabled={!mediaUrl || loading || downloading}
            onClick={handleDownload}
          >
            <i className={downloading ? 'ti ti-loader shm-loader-spin' : 'ti ti-download'} />
          </button>
          <button
            type="button"
            className="btn btn-blue btn-sm alerts-recording-viewer-icon-btn"
            title="Replay"
            aria-label="Replay"
            disabled={!mediaUrl || loading || !!error}
            onClick={handleReplay}
          >
            <i className="ti ti-repeat" />
          </button>
          <button
            type="button"
            className="btn btn-gray btn-sm alerts-recording-viewer-icon-btn"
            title="Close"
            aria-label="Close"
            onClick={handleClose}
          >
            <i className="ti ti-x" />
          </button>
        </div>
      </div>

      <div className="alerts-recording-viewer-body">
        {mediaUrl && !error && (
          <video
            ref={videoRef}
            className="alerts-recording-viewer-video"
            src={mediaUrl}
            controls
            autoPlay
            playsInline
            preload="metadata"
            onPlaying={() => { playedRef.current = true; }}
            onEnded={() => { playedRef.current = true; }}
            onError={handleMediaError}
          />
        )}
        {loading && (
          <div className="alerts-recording-viewer-loading">
            <Loading message="Loading video..." />
          </div>
        )}
        {!loading && error && (
          <div className="alerts-recording-viewer-error">
            <p>{error}</p>
            <button type="button" className="btn btn-blue btn-sm" onClick={() => loadUrl()}>
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
